import { useEffect, useState } from 'react';
import i18next from 'i18next';

import partnerInvoiceService from '@services/partnerInvoice';

import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';

import { exportPartnerInvoiceErrors } from '../common/utils/partnerInvoiceErrorFile';
import ConfirmationModal from '../../components/ConfirmationModal';
import PartnerInvoiceErrorsModal from '../components/PartnerInvoiceErrorsModal';
import PennylaneGenerateModal from '../components/PennylaneGenerateModal';

export const PENNYLANE_FLOW_MODE = {
  CK: 'CK',
  ORDERS: 'ORDERS',
};

export const useClientPennylaneIntegrationStatus = (enabled) => {
  const [hasClientPennylaneIntegration, setHasClientPennylaneIntegration] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHasClientPennylaneIntegration(false);
      return undefined;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const { hasPennylaneIntegration } =
          await partnerInvoiceService.fetchClientPennylaneStatus();

        if (cancelled) {
          return;
        }

        setHasClientPennylaneIntegration(!!hasPennylaneIntegration);
      } catch {
        if (cancelled) {
          return;
        }

        setHasClientPennylaneIntegration(false);
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return hasClientPennylaneIntegration;
};

export const useCentralKitchenStoreIdsWithPennylane = (enabled) => {
  const [centralKitchenStoreIdsWithPennylane, setCentralKitchenStoreIdsWithPennylane] = useState(
    [],
  );

  useEffect(() => {
    if (!enabled) {
      setCentralKitchenStoreIdsWithPennylane([]);
      return undefined;
    }

    let cancelled = false;

    const fetchStoreIds = async () => {
      try {
        const { centralKitchenStoreIdsWithPennylane: storeIds } =
          await partnerInvoiceService.fetchCentralKitchenStoreIdsWithPennylane();

        if (cancelled) {
          return;
        }

        setCentralKitchenStoreIdsWithPennylane(storeIds ?? []);
      } catch {
        if (cancelled) {
          return;
        }

        setCentralKitchenStoreIdsWithPennylane([]);
      }
    };

    fetchStoreIds();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return centralKitchenStoreIdsWithPennylane;
};

const _groupOrderIdsByCkIssuerRef = (selectedOrders) =>
  selectedOrders.reduce((acc, order) => {
    const centralKitchenIssuerRef = order.supplierId;

    if (!acc[centralKitchenIssuerRef]) {
      acc[centralKitchenIssuerRef] = [];
    }

    acc[centralKitchenIssuerRef].push(order.id);

    return acc;
  }, {});

const _reduceSuccessCount = (results) =>
  results.reduce((sum, item) => sum + (item.successCount ?? 0), 0);

const _flattenFailedStoreIds = (results) =>
  results.flatMap((item) => item.failedReceivingStoreIds ?? []);

/**
 * Shared orchestration for generating Pennylane partner invoices from either the CK preparation
 * page or the classic PDV orders page. Handles prerequisites check, error modal / XLSX export,
 * generation, and success/partial/failure feedback. Integration status is fetched separately
 * by `useClientPennylaneIntegrationStatus` / `useCentralKitchenStoreIdsWithPennylane`.
 *
 * @param {Object} params
 * @param {'CK'|'ORDERS'} params.mode
 * @param {Function} params.reloadOrders
 * @param {Function} params.onOrdersSelectedChange
 * @param {Function} params.openGenericModal
 * @param {Function} params.closeGenericModal
 * @param {Function} params.showMessage
 * @param {Function} params.showSuccessMessage
 * @param {Function} params.showErrorMessage
 * @param {Function} params.pageLoading
 * @param {Function} params.pageLoaded
 */
export const usePennylanePartnerInvoiceFlow = ({
  mode,
  reloadOrders,
  onOrdersSelectedChange,
  openGenericModal,
  closeGenericModal,
  showMessage,
  showSuccessMessage,
  showErrorMessage,
  pageLoading,
  pageLoaded,
}) => {
  const openConnectionErrorModal = () => {
    openGenericModal({
      type: 'error',
      width: '542px',
      height: 'auto',
      icon: '/images/inpulse/warning-white-small.svg',
      title: i18next.t('ORDERS.ORDERS.PENNYLANE_CONNECTION_ERROR_TITLE'),
      component: ConfirmationModal,
      data: {
        content: i18next.t('ORDERS.ORDERS.PENNYLANE_CONNECTION_ERROR_CONTENT'),
      },
      actions: [
        {
          key: 0,
          color: 'inpulse-default',
          label: i18next.t('GENERAL.OK'),
          handleClick: () => closeGenericModal(),
        },
      ],
    });
  };

  const openPrerequisiteErrorsModal = (errors, selectedOrders) => {
    const ordersById = selectedOrders.reduce((acc, order) => {
      acc[order.id] = order;

      return acc;
    }, {});

    openGenericModal({
      type: 'error',
      width: '542px',
      height: 'auto',
      icon: '/images/inpulse/warning-white-small.svg',
      title: i18next.t('ORDERS.ORDERS.GENERATE_PARTNER_INVOICES_ERROR_TITLE'),
      component: PartnerInvoiceErrorsModal,
      data: {
        onDownload: () => exportPartnerInvoiceErrors(errors, ordersById),
      },
      actions: [
        {
          key: 0,
          color: 'inpulse-default',
          label: i18next.t('GENERAL.CLOSE'),
          icon: '/images/inpulse/close-white-small.svg',
          handleClick: () => closeGenericModal(),
        },
      ],
    });
  };

  const _checkPrerequisites = (selectedOrders) => {
    if (mode === PENNYLANE_FLOW_MODE.CK) {
      const byIssuer = _groupOrderIdsByCkIssuerRef(selectedOrders);

      return Promise.all(
        Object.entries(byIssuer).map(([centralKitchenIssuerRef, orderIds]) =>
          partnerInvoiceService.checkPennylanePartnerInvoicePrerequisites({
            centralKitchenIssuerRef,
            orderIds,
          }),
        ),
      );
    }

    const orderIds = selectedOrders.map(({ id }) => id);

    return Promise.all([partnerInvoiceService.checkPennylaneOrdersPrerequisites({ orderIds })]);
  };

  const _generate = (selectedOrders, groupByStore) => {
    if (mode === PENNYLANE_FLOW_MODE.CK) {
      const byIssuer = _groupOrderIdsByCkIssuerRef(selectedOrders);

      return Promise.all(
        Object.entries(byIssuer).map(([centralKitchenIssuerRef, orderIds]) =>
          partnerInvoiceService.generatePennylanePartnerInvoices({
            centralKitchenIssuerRef,
            orderIds,
            groupByStore,
          }),
        ),
      );
    }

    const orderIds = selectedOrders.map(({ id }) => id);

    return Promise.all([
      partnerInvoiceService.generatePennylaneOrdersInvoices({ orderIds, groupByStore }),
    ]);
  };

  const handleGenerate = async (selectedOrders, groupByStore) => {
    pageLoading();

    try {
      const prerequisiteResults = await _checkPrerequisites(selectedOrders);

      const hasConnectionError = prerequisiteResults.some((result) => result.connectionError);

      if (hasConnectionError) {
        openConnectionErrorModal();
        return;
      }

      const allPrerequisiteErrors = prerequisiteResults.flatMap((result) => result.errors ?? []);

      if (allPrerequisiteErrors.length > 0) {
        openPrerequisiteErrorsModal(allPrerequisiteErrors, selectedOrders);
        return;
      }

      const results = await _generate(selectedOrders, groupByStore);

      const successCountTotal = _reduceSuccessCount(results);
      const failedReceivingStoreIds = _flattenFailedStoreIds(results);

      if (failedReceivingStoreIds.length > 0 && successCountTotal > 0) {
        showMessage(
          i18next.t('ORDERS.ORDERS.GENERATE_PARTNER_INVOICES_PARTIAL_WARNING', {
            failedCount: failedReceivingStoreIds.length,
            successCount: successCountTotal,
          }),
          'warning',
        );
      } else if (failedReceivingStoreIds.length > 0) {
        showErrorMessage(i18next.t('ORDERS.ORDERS.GENERATE_PARTNER_INVOICES_FAILURE'));
      } else {
        showSuccessMessage(i18next.t('ORDERS.ORDERS.GENERATE_PARTNER_INVOICES_SUCCESS'));
      }

      onOrdersSelectedChange([]);

      await reloadOrders();
    } catch {
      showErrorMessage(i18next.t('ORDERS.ORDERS.GENERATE_PARTNER_INVOICES_FAILURE'));
    } finally {
      pageLoaded();
    }
  };

  const openGenerateModal = (selectedOrders) => {
    const groupByStoreRef = { current: false };

    const storeCount = new Set(selectedOrders.map((order) => order.storeId)).size;
    const orderCount = selectedOrders.length;

    openGenericModal({
      type: 'action',
      width: '542px',
      height: 'auto',
      icon: '/images/invoice.svg',
      title: i18next.t('ORDERS.ORDERS.ACTION_GENERATE_PARTNER_INVOICES_PENNYLANE'),
      component: PennylaneGenerateModal,
      data: {
        storeCount,
        orderCount,
        onGroupByStoreChange: (value) => {
          groupByStoreRef.current = value;
        },
      },
      actions: [
        {
          ...GENERIC_MODAL_CANCEL_BUTTON(),
          handleClick: () => closeGenericModal(),
        },
        {
          key: 1,
          color: 'inpulse-default',
          label: i18next.t('ORDERS.ORDERS.PENNYLANE_GENERATE_MODAL_GENERATE_BUTTON'),
          icon: '/images/inpulse/done-white-small.svg',
          handleClick: () => {
            closeGenericModal();
            handleGenerate(selectedOrders, groupByStoreRef.current);
          },
        },
      ],
    });
  };

  return {
    openGenerateModal,
  };
};
